import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { IconDrivers } from '../components/icons';
import {
  Avatar,
  Chips,
  DataTable,
  DriverStatusPill,
  Empty,
  ErrorNote,
  Loading,
  PageHead,
  Pager,
  Pill,
  RowCard,
  SearchField,
  VehicleTile,
  type Column,
} from '../components/ui';
import { api } from '../lib/api';
import { ago, day, DOCUMENT_LABEL, phone, VEHICLE_LABEL } from '../lib/format';
import { useDashboard } from '../lib/queries';
import { qk } from '../lib/realtime-cache';
import type { DocumentKind, DriverListItem, DriverStatus, Page, VehicleType } from '../lib/types';
import { driversRoute } from '../router';

type Filter = 'ALL' | DriverStatus;
const DOCS: DocumentKind[] = ['DRIVING_LICENCE', 'VEHICLE_RC', 'AADHAAR'];

function DocDots({ d }: { d: DriverListItem }) {
  const have = new Set(d.documents.map((x) => x.kind));
  return (
    <span className="doc-dots" aria-hidden="true">
      {DOCS.map((k) => (
        <span key={k} className={have.has(k) ? '' : 'no'} />
      ))}
    </span>
  );
}

const docsText = (d: DriverListItem) =>
  d.missing.length === 0 ? 'Complete' : `Missing ${d.missing.map((m) => DOCUMENT_LABEL[m] ?? m).join(', ')}`;

export function DriversPage() {
  const search = driversRoute.useSearch();
  const nav = driversRoute.useNavigate();
  const navigate = useNavigate();
  const dash = useDashboard();
  const page = search.page ?? 1;
  const [text, setText] = useState(search.q ?? '');

  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? '') !== text.trim()) void nav({ search: { ...search, q: text.trim() || undefined, page: undefined } });
    }, 300);
    return () => clearTimeout(t);
  }, [text, search, nav]);

  const q = useQuery({
    queryKey: [...qk.drivers, search],
    queryFn: () =>
      api.get<Page<DriverListItem>>('/admin/drivers', {
        status: search.status,
        online: search.online ? 'true' : undefined,
        vehicleType: search.type,
        search: search.q,
        page,
        pageSize: 25,
      }),
    placeholderData: keepPreviousData,
  });
  const by = dash.data?.drivers.byStatus ?? {};
  const open = (d: DriverListItem) => navigate({ to: '/drivers/$driverId', params: { driverId: d.id } });

  const columns: Column<DriverListItem>[] = [
    {
      key: 'driver',
      header: 'Driver',
      cell: (d) => (
        <div className="person">
          <Avatar name={d.name} />
          <div style={{ minWidth: 0 }}>
            <div className="primary truncate">{d.name ?? 'No name yet'}</div>
            <div className="secondary">
              {d.phone ? phone(d.phone) : (d.email ?? 'No phone yet')} · joined {day(d.createdAt)}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'vehicle',
      header: 'Vehicle',
      cell: (d) =>
        d.vehicle ? (
          <div className="person">
            <VehicleTile type={d.vehicle.type} small />
            <div>
              <div>{VEHICLE_LABEL[d.vehicle.type]}</div>
              <div className="secondary nowrap">{d.vehicle.plateDisplay}</div>
            </div>
          </div>
        ) : (
          <span className="faint">Not added</span>
        ),
    },
    {
      key: 'docs',
      header: 'Documents',
      className: 'wide-only',
      cell: (d) => (
        <span className="row small" style={{ gap: 8, flexWrap: 'nowrap' }}>
          <DocDots d={d} />
          <span className={d.missing.length ? 'muted' : ''}>{docsText(d)}</span>
        </span>
      ),
    },
    {
      key: 'online',
      header: 'Online',
      cell: (d) =>
        d.isOnline ? (
          <span className="row small" style={{ gap: 6, flexWrap: 'nowrap' }}>
            <span className="dot on" /> {ago(d.location?.at)}
          </span>
        ) : (
          <span className="small faint">Offline</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (d) => (
        <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
          <DriverStatusPill status={d.status} />
          {d.status === 'PENDING' && d.missing.length === 0 && <Pill tone="accent">Ready</Pill>}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHead title="Drivers" sub="Review new drivers, and approve, reject or suspend with a reason." />
      <div className="toolbar">
        <Chips<Filter>
          label="Filter by status"
          value={search.status ?? 'ALL'}
          options={[
            { value: 'ALL', label: 'All' },
            { value: 'PENDING', label: 'Waiting for review', count: by.PENDING },
            { value: 'APPROVED', label: 'Approved' },
            { value: 'SUSPENDED', label: 'Suspended' },
            { value: 'REJECTED', label: 'Rejected' },
          ]}
          onChange={(v) => nav({ search: { ...search, status: v === 'ALL' ? undefined : v, page: undefined } })}
        />
      </div>
      <div className="toolbar">
        <Chips
          label="Online"
          value={search.online ? 'on' : 'any'}
          options={[
            { value: 'any', label: 'Everyone' },
            { value: 'on', label: 'Online now', count: dash.data?.drivers.online },
          ]}
          onChange={(v) => nav({ search: { ...search, online: v === 'on' ? true : undefined, page: undefined } })}
        />
        <Chips<'ANY' | VehicleType>
          label="Vehicle"
          value={search.type ?? 'ANY'}
          options={[
            { value: 'ANY', label: 'Every vehicle' },
            { value: 'BIKE', label: 'Bike' },
            { value: 'AUTO', label: 'Auto' },
            { value: 'CAR', label: 'Car' },
          ]}
          onChange={(v) => nav({ search: { ...search, type: v === 'ANY' ? undefined : v, page: undefined } })}
        />
        <SearchField value={text} onChange={setText} placeholder="Search name, phone, email or plate" label="Search drivers" />
      </div>
      <section className="card">
        {q.isLoading ? (
          <Loading rows={6} />
        ) : q.error ? (
          <div className="card-body">
            <ErrorNote error={q.error} />
          </div>
        ) : !q.data?.items.length ? (
          <Empty title="No drivers here" icon={IconDrivers}>
            Try another filter or search.
          </Empty>
        ) : (
          <>
            <DataTable
              label="Drivers"
              rows={q.data.items}
              columns={columns}
              rowKey={(d) => d.id}
              onRowClick={open}
              card={(d) => (
                <RowCard
                  lead={<Avatar name={d.name} />}
                  title={
                    <>
                      <span className="truncate">{d.name ?? 'No name yet'}</span>
                      {d.isOnline && <span className="dot on" aria-label="Online" />}
                    </>
                  }
                  sub={`${d.vehicle ? `${VEHICLE_LABEL[d.vehicle.type]} · ${d.vehicle.plateDisplay}` : 'No vehicle'} · ${docsText(d)}`}
                  side={
                    <>
                      <DriverStatusPill status={d.status} />
                      {d.status === 'PENDING' && d.missing.length === 0 && <Pill tone="accent">Ready</Pill>}
                    </>
                  }
                />
              )}
            />
            <Pager page={page} pageSize={q.data.pageSize} total={q.data.total} onPage={(p) => nav({ search: { ...search, page: p } })} />
          </>
        )}
      </section>
    </>
  );
}
